// src/apollo/client.ts
import { ApolloClient, InMemoryCache, from, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getApiUrl } from "../helpers";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { brandConfig } from "../brandConfig";

// Create the upload link
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const uploadLink = (createUploadLink as any)({
  uri: getApiUrl(),
});

/**
 * ACCESS_BLOCKED (2026-09-04): the backend's auth middleware refuses EVERY request
 * from an account whose sign-in has been switched off (terminated employee / admin
 * block) — including a session that was valid a minute ago. Without this, that
 * person stays "logged in" on a page of failing queries. Clear the token and send
 * them to /login?blocked=1, which explains what happened. Tenant-owned app shell —
 * ported by hand per the authentication module's CLAUDE.md (not carried by sync).
 */
const handleAccessBlocked = () => {
  try { localStorage.removeItem("auth_token"); } catch { /* storage unavailable — still redirect */ }
  const onLogin = window.location.pathname.startsWith("/login");
  if (!onLogin) window.location.assign("/login?blocked=1");
};

// Create error link to log all GraphQL errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  console.log('🔴 ==================== GRAPHQL ERROR ====================');
  console.log('Operation:', operation.operationName);

  if (graphQLErrors?.some((e) => e?.extensions?.code === "ACCESS_BLOCKED")) {
    // Only an AUTHENTICATED request (token present) means a live session was refused;
    // the login mutations themselves surface ACCESS_BLOCKED inside the modal.
    const hadToken = !!(operation.getContext().headers?.authorization);
    if (hadToken) handleAccessBlocked();
  }
  
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      console.error('Extensions:', extensions);
    });
  }

  if (networkError) {
    console.error('Network error:', networkError);
    // Type assertion to access response property
    const netError = networkError as any;
    if (netError.response) {
      console.error('Response status:', netError.response.status);
      console.error('Response body:', netError.response.body);
    }
  }
  
  console.log('🔴 ========================================================');
});

// Create the auth link to inject headers
const authLink = setContext((operation, { headers }) => {
  const token = localStorage.getItem("auth_token");
  
  const operationName = operation.operationName;
  const isFileUpload = operationName?.includes("Upload") || operationName?.includes("upload");
  
  // Always include tenant ID - even public operations need it for multi-tenant isolation
  // The tenant middleware will allow public operations to proceed without auth
  // Per-query override: a query can pass context.headers["x-tenant-id"] to scope
  // to a different tenant (used by master FE pages that audit any tenant, e.g.
  // /tenant-management/pay-audit).
  const incomingTenantId = (headers as any)?.["x-tenant-id"];
  const finalHeaders = {
    ...headers,
    "x-tenant-id": incomingTenantId || brandConfig.tenantId,
    "apollo-require-preflight": "true",
    "x-apollo-operation-name": operationName,
    // Don't set content-type for file uploads - apollo-upload-client handles it automatically
    ...(isFileUpload ? {} : { "content-type": "application/json" }),
    authorization: token ? `Bearer ${token}` : "",
  };

  console.log(`📡 GraphQL Request: ${operationName}`);

  return { headers: finalHeaders };
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        services: {
          merge(_, incoming) {
            return [...incoming];
          },
          keyArgs: false,
        }
      }
    }
  }
});

// Create WebSocket link
const wsLink = new GraphQLWsLink(createClient({
  url: (getApiUrl()).replace("http", "ws"), // Convert HTTP URL to WebSocket URL
  connectionParams: () => ({
    "x-tenant-id": brandConfig.tenantId,
    authorization: localStorage.getItem("auth_token") ? `Bearer ${localStorage.getItem("auth_token")}` : "",
  }),
}));

// Combine the links - INCLUDE ERROR LINK!
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  from([errorLink, authLink, uploadLink])  // Added errorLink here!
);

// Update Apollo Client configuration
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  }
});

console.log("API URL:", getApiUrl());
console.log("Tenant ID:", brandConfig.tenantId);